/* The characters kept on this device (up to 5), in localStorage. */

import type { CharacterStore } from './CharacterCreator';
import { Character, emptyCharacter } from './model';

const KEY = 'dhtools-characters';
// Older versions kept one character here; it moves into the list the first time the list is read.
const OLD_KEY = 'dhtools-character';

export const MAX_CHARACTERS = 5;

export interface SavedCharacter {
  id: string;
  data: Character;
}

/** A saved character with any field added since it was saved filled in. */
const withDefaults = (saved: Partial<Character>): Character => ({ ...emptyCharacter(), ...saved });

const newId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

/** Returns false when the browser refuses to store it (private mode, full storage). */
function writeCharacters(list: SavedCharacter[]): boolean {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

function moveOldCharacter() {
  const old = localStorage.getItem(OLD_KEY);
  if (!old) return;
  const list: SavedCharacter[] = JSON.parse(localStorage.getItem(KEY) ?? '[]');
  if (writeCharacters([{ id: newId(), data: JSON.parse(old) }, ...list])) localStorage.removeItem(OLD_KEY);
}

export function loadCharacters(): SavedCharacter[] {
  try {
    moveOldCharacter();
    const list: SavedCharacter[] = JSON.parse(localStorage.getItem(KEY) ?? '[]');
    return list.map(({ id, data }) => ({ id, data: withDefaults(data) }));
  } catch {
    return [];
  }
}

/** A store for one character of the list; a new one (`id` null) joins the list on its first save, while there is room. */
export function characterStore(id: string | null): CharacterStore {
  let slot = id;
  return {
    load: () => loadCharacters().find((c) => c.id === slot)?.data ?? null,
    save: (data) => {
      const list = loadCharacters();
      const at = list.findIndex((c) => c.id === slot);
      if (at >= 0) list[at] = { id: list[at].id, data };
      else if (list.length < MAX_CHARACTERS) list.push({ id: (slot ??= newId()), data });
      else return false;
      return writeCharacters(list);
    },
    remove: () => {
      const list = loadCharacters();
      if (list.some((c) => c.id === slot)) writeCharacters(list.filter((c) => c.id !== slot));
    },
  };
}
